import { useCallback, useContext, useEffect } from 'react'
import type { BookmarkPayload } from 'tripPlanner/api/bookmark/types'
import { useProcessBookmarks } from 'tripPlanner/hooks/api/bookmark'
import useBookmarkSnackbarHandlers from 'tripPlanner/hooks/bookmarks/useSavedItemSnackbarHandlers'
import type { BasicTrip } from 'tripPlanner/types/common'
import type { SaveItemsCallbackResult } from 'tripPlanner/components/Bookmark/BookmarkButton/utils'
import { clearRecentlySavedTripId } from 'storage/recentSavedTrip'
import ModalContext from 'contexts/ModalContext'
import type { TripPlannerSaveModalResult } from 'tripPlanner/components/Bookmark/Common/SaveModal'
import SaveModal from 'tripPlanner/components/Bookmark/Common/SaveModal'
import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'
import { recordOfferView, recordSavePromptShown } from 'storage/savePrompt'
import useToggle from 'hooks/useToggle'
import StickyPrompt from 'components/Common/StickyPromptCard/StickyPrompt'
import Group from 'components/utils/Group'
import TextButton from 'LuxKit/Button/TextButton'
import styled from 'styled-components'
import { rem } from 'polished'
import { getSimpleImageUrl } from 'lib/image/imageUtils'
import { trackClientEvent } from 'analytics/analytics'
import { AnalyticsPage } from 'contexts/Analytics/analyticsPageContext'
import usePendingLoginHandler from 'hooks/usePendingLoginHandler'
import Heading from 'LuxKit/Typography/Heading'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import LineHeartIcon from 'LuxKit/Icons/line/LineHeartIcon'
import { useAppSelector } from 'hooks/reduxHooks'
import { selectLoggedIn } from 'selectors/accountSelectors'
import { FormattedMessage, useIntl } from 'react-intl'

const CardRoot = styled(StickyPrompt)`
  padding: ${rem(16)} ${rem(20)};
  bottom: ${rem(16)};
  width: ${rem(580)};
`

const HeartIcon = styled.img`
  height: ${rem(68)};
  width: ${rem(68)};
  margin-top: ${rem(-12)};
`

interface Props {
  offerId: string
  offerTypeLabel: string
  bookmarkPayload: BookmarkPayload
  offerImageId?: string
  newTripName: string | undefined
  trips: Array<BasicTrip>
}

function SavePrompt({
  offerId,
  offerTypeLabel,
  bookmarkPayload,
  offerImageId,
  newTripName,
  trips,
}: Props) {
  const intl = useIntl()
  const { value: isPromptShown, on: showPrompt, off: hidePrompt } = useToggle()
  const isLoggedIn = useAppSelector(selectLoggedIn)

  const { showSaveErrorSnackbar } = useBookmarkSnackbarHandlers()

  const { mutateAsync: processBookmarks, isLoading: isCreating } =
    useProcessBookmarks({
      onError: (e, variables) => {
        showSaveErrorSnackbar(
          trips.find((t) => t.id === variables.tripIdsAdded[0])?.name,
        )

        // Clear the recently saved trip ID in case it errored because the trip was deleted
        clearRecentlySavedTripId()
      },
    })

  const createTripItems = useCallback(
    async(tripId: string): Promise<SaveItemsCallbackResult> => {
      const bookmarkResult = await processBookmarks({
        items: [bookmarkPayload],
        tripIdsAdded: [tripId],
        tripIdsRemoved: [],
      })

      return {
        savedItemIds: bookmarkResult.created.map((item) => item.id),
      }
    },
    [bookmarkPayload, processBookmarks],
  )

  const offerPageState = useContext(OfferPageStateContext)
  const showModal = useContext(ModalContext)
  const openSaveModal = useCallback(() => {
    hidePrompt()
    showModal<TripPlannerSaveModalResult>(
      // Need to forward the contexts and modals don't have access
      // We need access in the trip settings modal
      <OfferPageStateContext.Provider value={offerPageState}>
        <SaveModal
          createTripItems={createTripItems}
          itemTypeLabel="offer"
          defaultTripName={newTripName}
          offerImageId={offerImageId}
          isCreatingItem={isCreating}
        />
      </OfferPageStateContext.Provider>,
    )
    trackClientEvent({
      action: 'click',
      subject: 'save_to_trip_prompt',
      category: AnalyticsPage.tripPlanner,
      type: 'nonInteraction',
    })
  }, [
    createTripItems,
    isCreating,
    newTripName,
    offerImageId,
    offerPageState,
    showModal,
    hidePrompt,
  ])

  const onSaveButtonClick = usePendingLoginHandler(
    openSaveModal,
    'tripPlannerLogin',
  )

  useEffect(
    () => {
      if (!isLoggedIn) return

      const viewCountData = recordOfferView(offerId)
      if (!viewCountData.savePromptShownAt && viewCountData.viewCount >= 3) {
        const timeout = setTimeout(() => {
          showPrompt()
          recordSavePromptShown(offerId)
          trackClientEvent({
            action: 'appear',
            subject: 'save_to_trip_prompt',
            category: AnalyticsPage.tripPlanner,
            type: 'nonInteraction',
          })
        }, 5000)
        return () => { clearTimeout(timeout) }
      }
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [offerId, isLoggedIn],
  )

  return <CardRoot show={isPromptShown} promptLocation="bottom">
    <Group
      direction="vertical"
      gap={12}
      tabletDirection="horizontal"
      tabletHorizontalAlign="space-between"
      fullWidth
      tabletGap={20}
    >
      <Group
        direction="horizontal-reverse"
        horizontalAlign="space-between"
        tabletDirection="horizontal"
        tabletGap={20}
        tabletVerticalAlign="center"
      >
        <HeartIcon src={getSimpleImageUrl('animated-save-heart-v2')} />
        <Group direction="vertical">
          <Heading variant="heading5">
            <FormattedMessage
              id="83svJoFXnmUUow9XvHu-"
              defaultMessage="Save this {offerTypeLabel}"
              description="Heading prompting user to save the current offer to a trip"
              values={{ offerTypeLabel }}
            />
          </Heading>
          <BodyTextBlock variant="medium">
            {intl.formatMessage({
              id: 'PbCUcdPmit_BjdCe9_I7',
              defaultMessage: 'Keep it handy for when you’re ready to book.',
              description: 'Subtitle encouraging user to save the offer for later booking',
            })}
          </BodyTextBlock>
        </Group>
      </Group>

      <Group direction="horizontal" verticalAlign="center" gap={12} horizontalAlign="stretch" tabletHorizontalAlign="space-evenly">
        <TextButton kind="tertiary" size="medium" onClick={hidePrompt}>
          {intl.formatMessage({
            id: 'ErnRI550BhbKDopMhHgL',
            defaultMessage: 'No, thanks',
            description: 'Button to dismiss the save offer prompt on the offer listing or detail page',
          })}
        </TextButton>
        <TextButton kind="primary" size="medium" onClick={onSaveButtonClick} startIcon={<LineHeartIcon />}>
          {intl.formatMessage({
            id: 'phafRTkEJwBymasaWIa-',
            defaultMessage: 'Save offer',
            description: 'Button to save the current offer to a trip in the trip planner',
          })}
        </TextButton>
      </Group>
    </Group>
  </CardRoot>
}

export default SavePrompt
